// packages/ui/src/api/hooks.ts
// TanStack Query hooks for the dashboard API

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { useEffect } from 'react'
import {
  fetchCards,
  createCard,
  updateCard,
  deleteCard,
  fetchSessions,
  fetchSession,
  fetchActivity,
  apiRequest,
  type CardsResponse,
  type CardResponse,
  type SessionsResponse,
  type SessionResponse,
  type ActivityResponse,
  type CreateCardInput,
  type UpdateCardInput,
  type DashboardCard,
} from './client'

// ─── Query Keys ──────────────────────────────────────────────

export const queryKeys = {
  cards: (filters?: Record<string, string>) => ['cards', filters ?? {}] as const,
  sessions: (opts?: { limit?: number; offset?: number }) =>
    ['sessions', opts ?? {}] as const,
  session: (id: string) => ['sessions', id] as const,
  activity: (opts?: { limit?: number; offset?: number }) =>
    ['activity', opts ?? {}] as const,
}

// ─── Card Hooks ──────────────────────────────────────────────

export function useCards(
  filters?: { status?: string; priority?: string; source?: string },
  options?: Omit<UseQueryOptions<CardsResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.cards(filters as Record<string, string>),
    queryFn: () => fetchCards(filters),
    ...options,
  })
}

export function useCreateCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateCardInput) => createCard(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] })
    },
  })
}

export function useUpdateCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: UpdateCardInput) => updateCard(input),
    onMutate: async (input) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['cards'] })

      // Snapshot previous value
      const previous = queryClient.getQueriesData<CardsResponse>({
        queryKey: ['cards'],
      })

      // Optimistically update the cache
      queryClient.setQueriesData<CardsResponse>(
        { queryKey: ['cards'] },
        (old) => {
          if (!old) return old
          return {
            cards: old.cards.map((card: DashboardCard) =>
              card.id === input.id
                ? {
                    ...card,
                    ...(input.status ? { column: input.status } : {}),
                    ...(input.title ? { title: input.title } : {}),
                    ...(input.description != null
                      ? { description: input.description }
                      : {}),
                    ...(input.priority ? { priority: input.priority } : {}),
                  }
                : card,
            ),
          }
        },
      )

      return { previous }
    },
    onError: (_err, _input, context) => {
      // Rollback on error
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data)
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] })
    },
  })
}

export function useDeleteCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteCard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] })
    },
  })
}

// ─── Session Hooks ───────────────────────────────────────────

export function useSessions(
  opts: { limit?: number; offset?: number } = {},
  options?: Omit<UseQueryOptions<SessionsResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.sessions(opts),
    queryFn: () => fetchSessions(opts),
    ...options,
  })
}

export function useSession(
  id: string,
  options?: Omit<UseQueryOptions<SessionResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.session(id),
    queryFn: () => fetchSession(id),
    enabled: !!id,
    ...options,
  })
}

// ─── Activity Hooks ──────────────────────────────────────────

export function useActivity(
  opts: { limit?: number; offset?: number } = {},
  options?: Omit<UseQueryOptions<ActivityResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.activity(opts),
    queryFn: () => fetchActivity(opts),
    ...options,
  })
}

// ─── Visibility Refetch ──────────────────────────────────────

export function useVisibilityRefetch(queryKey: string[]) {
  const queryClient = useQueryClient()
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey })
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [queryClient, queryKey])
}

// ─── Dashboard Home ──────────────────────────────────────────

export function useHomeData() {
  return useQuery({
    queryKey: ['dashboard', 'home'],
    queryFn: () =>
      apiRequest<{
        counts: { awaitingApproval: number; failedCronsLast24h: number; upcomingCronsNextHour: number }
        recentActivity: Array<{ id: string; event_type: string; params: unknown; created_at: string }>
      }>('/home'),
    refetchInterval: 10_000,
  })
}

// ─── Approvals ───────────────────────────────────────────────

// ─── Dashboard Types ─────────────────────────────────────────

export interface Approval {
  id: string
  state: string
  mr_title: string
  mr_url: string
  project_id: number
  mr_iid: number
  created_at: string
  updated_at: string
  approved_by?: string
  approved_at?: string
  assess_summary?: string
  assess_findings?: string
}

export interface CCSession {
  name: string
  status: string
  workdir: string
  uptime: number
}

export interface CronJob {
  id: string
  name: string
  schedule: string
  prompt: string
  channel?: string
  enabled: boolean
  lastRun?: string
  nextRun?: string
  createdAt: string
}

export interface CronRun {
  id: string
  status: string
  started_at: string
  finished_at?: string
  output?: string
}

/** @deprecated Use `useApprovals()` (queryKey ['approvals']) for WS-invalidated data. This hook is kept for index.tsx home dashboard counts only. */
export function useApprovalsList() {
  return useQuery({
    queryKey: ['dashboard', 'approvals'],
    queryFn: () =>
      apiRequest<{ pending: Approval[]; resolved: Approval[] }>('/approvals'),
    refetchInterval: 10_000,
  })
}

export function useApprovals() {
  return useQuery({
    queryKey: ['approvals'],
    queryFn: async () => {
      return apiRequest<{
        pending: Array<{ id: string; kind: string; status: string; title: string; body: string; severity: number; expires_at: string; [key: string]: unknown }>
        resolved: Array<{ id: string; kind: string; status: string; title: string; [key: string]: unknown }>
      }>('/approvals')
    },
    refetchInterval: 30_000,
    staleTime: 10_000,
  })
}

export function useCCSessions() {
  return useQuery({
    queryKey: ['dashboard', 'cc-sessions'],
    queryFn: () => apiRequest<{ sessions: CCSession[] }>('/cc/sessions'),
    refetchInterval: 10_000,
  })
}

export function useCrons() {
  return useQuery({
    queryKey: ['dashboard', 'crons'],
    queryFn: () => apiRequest<{ crons: CronJob[] }>('/crons'),
    refetchInterval: 30_000,
  })
}

export function useCronRuns(id: string) {
  return useQuery({
    queryKey: ['dashboard', 'cron-runs', id],
    queryFn: () => apiRequest<{ runs: CronRun[] }>(`/crons/${id}/runs`),
    enabled: !!id,
  })
}

export function useTriggerCron() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ ok: true }>(`/crons/${id}/trigger`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', 'crons'] }),
  })
}

export function useToggleCron() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiRequest<CronJob>(`/crons/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', 'crons'] }),
  })
}

export function useApprovalDetail(id: string) {
  return useQuery({
    queryKey: ['dashboard', 'approvals', id],
    queryFn: () =>
      apiRequest<{
        id: string
        mr_url: string
        state: string
        project_id: number
        mr_title?: string
        assess_summary?: string
        assess_findings?: string
        created_at: string
        updated_at: string
      }>(`/approvals/renovate/${id}`),
    enabled: !!id,
  })
}

export function useDecideRenovate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approve' | 'reject' }) =>
      apiRequest<{ ok: boolean }>(`/approvals/renovate/${id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ decision }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'approvals'] })
    },
  })
}
